import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect
} from 'react';
import {
  Flex,
  Table,
  CollapsibleFilterBuilder
} from 'onefaceui-react-components';
import { useDispatch } from 'react-redux';
import defaultFetch from 'react/api/sagas/fetch/defaultFetch';
import { getResourceTo } from 'react/utils/utils';
import { getCriteriaChain } from 'react/utils/criteriaChainUtils';
import { getEquipmentAssignments } from 'react/api/selectors/quickAssignment';
import {
  addTagOptions,
  assignEquipmentToRoute,
  equipmentCanBeAssigned,
  isPrincipal
} from './utils';
import {
  addIntervalFields,
  getDefaultFilters,
  mapFieldsToFilterBuilder,
  saveFiltersByColumn
} from '../utils';
import { useEquipmentFields } from 'react/queries/fields/EquipmentFields';
import Loading from 'react/components/Loading';
import { Container } from './EquipmentListStyles';

function EquipmentList() {
  const FILTER_NAME = 'SpeedAssignmentEquipment';
  const [data, setData] = useState([]);
  const { defaultSelected, defaultValues } = getDefaultFilters(FILTER_NAME);
  const equipmentFieldsQuery = useEquipmentFields();
  const filtersRef = useRef(defaultValues || {});
  const filterBuilderFieldsRef = useRef([]);
  const tableRef = useRef(null);
  const dispatch = useDispatch();

  const filterBuilderFields = useMemo(() => {
    const fieldsData = equipmentFieldsQuery.data || [];

    const fieldsMapped = fieldsData.map((field) =>
      mapFieldsToFilterBuilder(field, fieldsData)
    );
    const fieldsWithTags = addTagOptions(fieldsMapped);

    return addIntervalFields(fieldsWithTags);
  }, [equipmentFieldsQuery.data]);

  const equipmentTableColumns = [
    {
      title: getResourceTo('Organization'),
      field: 'organization.key',
      width: 110
    },
    {
      title: getResourceTo('SpeedAssignment.EquipmentGrid.equipmentType'),
      field: 'equipmentType.key',
      width: 110
    },
    {
      title: getResourceTo('SpeedAssignment.EquipmentGrid.key'),
      field: 'key',
      width: 100
    },
    {
      field: 'assignedToNotCompletedRoute',
      title: 'five',
      width: 30,
      minWidth: 30,
      titleFormatter: 'tickCross',
      titleFormatterParams: {
        crossElement: '<div class="assignedToNotCompletedRoute" />'
      },
      formatter: 'tickCross',
      formatterParams: {
        allowEmpty: true,
        allowTruthy: true,
        tickElement: '<div class="gridCheckboxFalse"/>',
        crossElement: '<div class="gridCheckboxTrue" />'
      },
      headerSort: false
    }
  ];

  const columnsPreferences = [
    {
      field: 'key'
    },
    {
      field: 'equipmentType.key'
    },
    {
      field: 'organization.key'
    },
    {
      field: 'assignedToNotCompletedRoute'
    }
  ];

  useEffect(() => {
    if (equipmentFieldsQuery.isSuccess && equipmentFieldsQuery.data) {
      const fieldsData = equipmentFieldsQuery.data || [];

      const fieldsMapped = fieldsData.map((field) =>
        mapFieldsToFilterBuilder(field, fieldsData)
      );
      const fieldsWithTags = addTagOptions(fieldsMapped);
      const fields = addIntervalFields(fieldsWithTags);

      filterBuilderFieldsRef.current = fields;
    }
  }, [equipmentFieldsQuery]);

  const requestDataTable = useCallback(
    async (pageParam = 1, filtersOverride = null) => {
      if (tableRef?.current) {
        const criteria = {
          filters: [
            'id',
            'key',
            'equipmentType.key',
            'equipmentType.description',
            'equipmentType.id',
            'equipmentType.size1',
            'equipmentType.size2',
            'equipmentType.size3',
            'organization.key'
          ],
          firstResult: pageParam - 1 === 0 ? 0 : (pageParam - 1) * 51,
          maxResults: 51
        };

        const activeFilters = filtersOverride || filtersRef.current;
        const currentCriteriaChain = getCriteriaChain(
          activeFilters,
          filterBuilderFieldsRef.current,
          true
        );

        const url = `/Equipment/FindAssignmentAvailability?criteria=${encodeURIComponent(
          JSON.stringify(criteria)
        )}`;

        tableRef.current?.setLoading(true);
        const response = await defaultFetch(url, currentCriteriaChain, 'POST');
        tableRef.current?.setLoading(false);

        if (response.error) {
          return [];
        }

        return response.map(({ equipment, assignedToNotCompletedRoute }) => ({
          ...equipment,
          assignedToNotCompletedRoute
        }));
      }
    },
    [tableRef, filtersRef, filterBuilderFieldsRef]
  );

  const handleFilterSubmit = async (values) => {
    filtersRef.current = values;

    const mappedData = await requestDataTable(1, values);
    if (mappedData) {
      setData(mappedData);
      saveFiltersByColumn(FILTER_NAME, values);
    }
  };

  const handleDropEquipment = useCallback(
    (equipment, routeId) => {
      const routes = GreenmileReduxStore.getState()
        .quickAssignment.get('routes')
        .toJS();
      const route = routes.find((item) => item.id === Number(routeId));

      if (!route) {
        return;
      }

      const blockedToAdd =
        route.status === 'DEPARTED_ORIGIN' &&
        equipment.assignedToNotCompletedRoute;

      if (blockedToAdd) {
        return;
      }

      const equipments = getEquipmentAssignments(
        GreenmileReduxStore.getState(),
        route.id
      ).map((item) => ({
        ...item.equipment,
        principal: item.principal
      }));

      const canAssign = equipmentCanBeAssigned(equipment, equipments);

      if (!canAssign) {
        return;
      }

      Object.assign(equipment, {
        principal: isPrincipal(equipment, equipments)
      });

      assignEquipmentToRoute(equipment, route.id, dispatch);
    },
    [window.globalStore]
  );

  return (
    <Container dataTestId="equipment-list-container">
      <CollapsibleFilterBuilder
        defaultExpanded
        sectionTitleLabel={getResourceTo('SpeedAssignment.EquipmentList')}
        defaultValues={defaultValues}
        defaultSelected={defaultSelected}
        onSubmit={handleFilterSubmit}
        fields={filterBuilderFields}
        dataTestId="filter-builder"
        hasExpandedAction={!equipmentFieldsQuery.isLoading}
      />
      <Flex
        className="equipment-list-table"
        dataTestId="equipments-table-container"
      >
        {equipmentFieldsQuery.isLoading && <Loading />}
        {!equipmentFieldsQuery.isLoading && filterBuilderFields.length > 0 && (
          <Table
            ref={tableRef}
            tableOptions={{
              selectableRows: 0,
              movableRows: true,
              movableRowsConnectedElements: '.route-item'
            }}
            headerOptions={{ showResults: true }}
            defaultData={data}
            columns={equipmentTableColumns}
            columnsPreference={columnsPreferences}
            pagination={{
              pageSize: 51,
              mode: 'remote',
              type: 'scroll'
            }}
            requestData={(page) => requestDataTable(page)}
            events={{
              movableRowsElementDrop: (_, element, row) => {
                handleDropEquipment(row.getData(), element.dataset.routeid);
              }
            }}
            dataTestId="equipments-table"
            noDataMessage={getResourceTo('Greenmile.Grid.NoResult')}
          />
        )}
      </Flex>
    </Container>
  );
}

export default EquipmentList;
